import * as ReduxToolkit from "@reduxjs/toolkit";
import {
    WeeklyDriverShiftsRow,
    TripsReportsRow,
    NeighborPickupsRow,
    GoCancelsRow,
} from "../../../api";

export interface SelectedDriver {
    driver_id: number;
    driver_code: string;
    driver_name: string;
}

export interface SelectedDriverDetailHeaderInfo {
    comment: string;
    avg_revenue: number;
    office_avg_revenue: number;
    shifts: number;
    worst_shift_id: string;
    feedback_flag: boolean;
    week_no: number;
    year: number;
}

export interface TripReportsDict {
    [shiftId: string]: TripsReportsRow[];
}

export interface NeighborsDict {
    [tripId: string]: NeighborPickupsRow[];
}

export interface NeighborsPerTrip {
    shift: any;
    neighbors: NeighborsDict | any;
}

export interface TripDetails {
    trip_id: string;
    trip_start_ts: string;
}

export interface DetailReportState {
    preSelectedWorstDay: WeeklyDriverShiftsRow | null;
    detailReports: WeeklyDriverShiftsRow[] | null;
    selectedDriver: SelectedDriver | null;
    detailHeaderInfo: SelectedDriverDetailHeaderInfo | null;
    driverTripsByShift: TripsReportsRow[];
    lastNeighborsMessage: string | null;
    neighborsPerTrip: NeighborsPerTrip;
    viewingShift: string | null;
    neighborsViewingIsOn: boolean;
    selectedShiftId: string | null;
    selectedTripDetails: TripDetails | null;
    goCancels: GoCancelsData;
    expandedContainerName: string | null;
    expandedContainers: any[];
}

export interface SetTripsInterface {
    shiftId: string;
    trips: TripsReportsRow[];
}

export interface SetNeighborsInterface {
    tripId: string;
    shiftId: any;
    neighbors: NeighborPickupsRow[];
}

export interface GoCancelsData {
    data: GoCancelsRow[] | null;
    shiftId: any;
}

export const initDetailScreenSlice = (): DetailReportState => {
    return {
        preSelectedWorstDay: null,
        detailReports: null,
        selectedDriver: null,
        detailHeaderInfo: null,
        driverTripsByShift: [],
        lastNeighborsMessage: null,
        neighborsPerTrip: {
            shift: null,
            neighbors: {},
        },
        viewingShift: null,
        neighborsViewingIsOn: false,
        selectedShiftId: null,
        selectedTripDetails: null,
        goCancels: {
            data: null,
            shiftId: null,
        },
        expandedContainerName: null,
        expandedContainers: [],
    } as DetailReportState;
};

export const detailSceenSlice = ReduxToolkit.createSlice({
    name: "detail_screen_slice",
    initialState: initDetailScreenSlice() as DetailReportState,
    reducers: {
        resetState: (state) => {
            state = initDetailScreenSlice();
            return state;
        },
        setSelectedDriver: (
            state,
            action: ReduxToolkit.PayloadAction<SelectedDriver | null>
        ) => {
            state.selectedDriver = action.payload;
            return state;
        },
        setExpandedContainers: (
            state,
            action: ReduxToolkit.PayloadAction<any[] | null>
        ) => {
            if (!action.payload) {
                state.expandedContainers = [];
                return state;
            }
            state.expandedContainers = action.payload;
            return state;
        },
        setExpandedContainerName: (
            state,
            action: ReduxToolkit.PayloadAction<string | null>
        ) => {
            state.expandedContainerName = action.payload;
            return state;
        },
        setLastNeighborsMsg: (
            state,
            action: ReduxToolkit.PayloadAction<string | null>
        ) => {
            state.lastNeighborsMessage = action.payload;
            return state;
        },
        setDetailReports: (
            state,
            action: ReduxToolkit.PayloadAction<WeeklyDriverShiftsRow[] | null>
        ) => {
            state.detailReports = action.payload;
            return state;
        },
        setGoCancels: (
            state,
            action: ReduxToolkit.PayloadAction<GoCancelsData | null>
        ) => {
            if (!action.payload) {
                state.goCancels = {
                    data: null,
                    shiftId: null,
                };
                return state;
            }
            const goCancels: any = {};
            goCancels.data = action.payload.data;
            goCancels.shiftId = action.payload.shiftId;
            state.goCancels = goCancels;
            return state;
        },
        setDriverDetailHeaderInfo: (
            state,
            action: ReduxToolkit.PayloadAction<SelectedDriverDetailHeaderInfo | null>
        ) => {
            state.detailHeaderInfo = action.payload;
            return state;
        },
        setViewingShift: (
            state,
            action: ReduxToolkit.PayloadAction<string | null>
        ) => {
            state.viewingShift = action.payload;
            return state;
        },
        setTrip: (
            state,
            action: ReduxToolkit.PayloadAction<TripDetails | null>
        ) => {
            state.selectedTripDetails = action.payload;
            return state;
        },
        setDriverTrips: (
            state,
            action: ReduxToolkit.PayloadAction<TripsReportsRow[] | null>
        ) => {
            if (!action.payload) {
                state.driverTripsByShift = [];
                return state;
            }
            state.driverTripsByShift = action.payload;
            return state;
        },
        setNeighborsPerTrip: (
            state,
            action: ReduxToolkit.PayloadAction<SetNeighborsInterface | null>
        ) => {
            if (!action.payload) {
                state.neighborsPerTrip = {
                    shift: null,
                    neighbors: {},
                };
                return state;
            }
            const neighbordict: NeighborsDict = {};
            neighbordict[action.payload.tripId] = action.payload.neighbors;
            state.neighborsPerTrip = {
                shift: action.payload.shiftId,
                neighbors: neighbordict,
            };

            return state;
        },
        setNeighborsViewingOn: (
            state,
            action: ReduxToolkit.PayloadAction<boolean>
        ) => {
            state.neighborsViewingIsOn = action.payload;
            return state;
        },
        setSelectedShiftId: (
            state,
            action: ReduxToolkit.PayloadAction<string | null>
        ) => {
            state.selectedShiftId = action.payload;
            return state;
        },
    },
});
